import React, { useState, useEffect } from 'react';
import { Table, Badge, Alert, Spinner, Button, Collapse } from 'react-bootstrap';
import { FaFile, FaFileImage, FaFilePdf, FaFileWord, FaFileExcel, FaFileArchive, FaFileCode, FaDownload, FaStar, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { getUserSubmissions, getFileUrl } from '../services/submissionService';
import { formatDateTime } from '../utils/dateUtils';
import './UserSubmissions.scss';

const UserSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSubmissions, setExpandedSubmissions] = useState(new Set());

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const userSubmissions = await getUserSubmissions();
      
      // Sort submissions by timestamp (newest first)
      const sortedSubmissions = userSubmissions.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      
      setSubmissions(sortedSubmissions);
      setError('');
    } catch (err) {
      setError('Failed to load your submissions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to determine if a submission is the latest for its track
  const getLatestSubmissionsByTrack = () => {
    const latestByTrack = {};
    
    // Group submissions by track and find the latest for each
    submissions.forEach(submission => {
      const track = submission.track;
      if (!latestByTrack[track] || 
          new Date(submission.timestamp) > new Date(latestByTrack[track].timestamp)) {
        latestByTrack[track] = submission;
      }
    });
    
    return latestByTrack;
  };

  const latestByTrack = getLatestSubmissionsByTrack();
  
  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const getFileIcon = (file) => {
    // Check if file or file.name is undefined
    if (!file || !file.name) {
      return <FaFile className="file-icon" />;
    }
    
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (file.type && file.type.startsWith('image/')) return <FaFileImage className="file-icon" />;
    if (extension === 'pdf') return <FaFilePdf className="file-icon" />;
    if (['doc', 'docx'].includes(extension)) return <FaFileWord className="file-icon" />;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return <FaFileExcel className="file-icon" />;
    if (['zip', 'rar', 'tar', 'gz'].includes(extension)) return <FaFileArchive className="file-icon" />;
    if (['js', 'py', 'java', 'html', 'css', 'php', 'c', 'cpp', 'h', 'rb', 'json'].includes(extension)) {
      return <FaFileCode className="file-icon" />;
    }
    
    return <FaFile className="file-icon" />;
  };
  
  const handleDownload = async (file) => {
    try {
      const url = await getFileUrl(file.key);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file. Please try again.');
    }
  };
  
  const toggleExpandSubmission = (submissionId) => {
    const newExpanded = new Set(expandedSubmissions);
    if (newExpanded.has(submissionId)) {
      newExpanded.delete(submissionId);
    } else {
      newExpanded.add(submissionId);
    }
    setExpandedSubmissions(newExpanded);
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (submissions.length === 0) {
    return (
      <div className="user-submissions">
        <Alert variant="info">
          <Alert.Heading>No submissions yet</Alert.Heading>
          <p>
            You haven't submitted any solutions yet. Your most recent submission for each track will be the one that gets graded.
          </p>
          <hr />
          <p className="mb-0">
            Ready to submit? <Alert.Link href="/user/submit">Go to the submission form</Alert.Link> to submit your first solution!
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="user-submissions">
      <div className="d-flex justify-content-between align-items-center mb-3">
        {submissions.length > 0 && (
          <small className="text-muted">
            <FaStar className="text-warning me-1" />
            Most recent submission per track will be graded
          </small>
        )}
      </div>
      
      <Table responsive striped hover className="submissions-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Track</th>
            <th>Submitted On</th>
            <th>Files</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((submission, index) => {
            const isLatestForTrack = latestByTrack[submission.track]?.submissionId === submission.submissionId;
            const isExpanded = expandedSubmissions.has(submission.submissionId);
            const hasFiles = submission.files && submission.files.length > 0;
            
            return (
              <React.Fragment key={submission.submissionId}>
                <tr className={isLatestForTrack ? 'table-warning' : ''}>
                  <td>
                    <div className="d-flex align-items-center">
                      {isLatestForTrack && (
                        <FaStar 
                          className="text-warning me-2" 
                          title={`This is your latest submission for ${submission.track} and will be graded`}
                        />
                      )}
                      <div>
                        <div className="submission-title">{submission.title}</div>
                        {submission.authors && submission.authors.length > 0 && (
                          <div className="submission-authors">
                            <small className="text-muted">
                              Authors: {submission.authors.map(author => 
                                author.affiliation 
                                  ? `${author.name} (${author.affiliation})`
                                  : author.name
                              ).join('; ')}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge bg="secondary">{submission.track}</Badge>
                  </td>
                  <td>
                    {formatDateTime(submission.timestamp)}
                    {isLatestForTrack && (
                      <div>
                        <small className="text-success fw-bold">Latest for {submission.track}</small>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <span className="me-2">{submission.fileCount || 0} files</span>
                      {hasFiles && (
                        <Button 
                          variant="link" 
                          size="sm"
                          className="p-0 text-decoration-none"
                          onClick={() => toggleExpandSubmission(submission.submissionId)}
                          title={isExpanded ? 'Hide files' : 'Show files'}
                        >
                          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </Button>
                      )}
                    </div>
                  </td>
                  <td>
                    {hasFiles && (
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => toggleExpandSubmission(submission.submissionId)}
                      >
                        {isExpanded ? 'Hide Files' : 'View Files'}
                      </Button>
                    )}
                  </td>
                </tr>
                
                {/* Files row - only show if expanded and has files */}
                {hasFiles && (
                  <tr className={`files-row ${isLatestForTrack ? 'table-warning' : ''}`}>
                    <td colSpan="5" className="p-0">
                      <Collapse in={isExpanded}>
                        <div className="files-container p-3 bg-light">
                          <div className="files-grid">
                            {submission.files.map((file, fileIndex) => (
                              <div key={fileIndex} className="file-item d-flex align-items-center justify-content-between p-2 border rounded mb-2 bg-white">
                                <div className="file-info d-flex align-items-center">
                                  {getFileIcon(file)}
                                  <div className="file-details ms-2">
                                    <div className="file-name fw-medium">{file.name}</div>
                                    <div className="file-metadata text-muted small">
                                      <div>{formatFileSize(file.size)}</div>
                                      {false && file.lastModified && (
                                        <div>Created: {new Date(file.lastModified).toLocaleString()}</div>
                                      )}
                                      {file.md5Hash && (
                                        <div className="font-monospace">MD5: {file.md5Hash}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  onClick={() => handleDownload(file)}
                                  className="download-button"
                                >
                                  <FaDownload className="me-1" /> Download
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default UserSubmissions;
